import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import AdminLogin from './components/AdminLogin';
import PublicMatches from './components/PublicMatches';
import Dashboard from './components/Dashboard';
import Teams from './components/Teams';
import Matches from './components/Matches';
import MatchDetail from './components/MatchDetail';
import CreateMatchPage from './components/creatematch/CreateMatchPage';
import Servers from './components/Servers';
import Maps from './components/Maps';
import Admins from './components/Admins';
import Seasons from './components/Seasons';
import SeasonPage from './components/seasons/SeasonPage';
import PublicSeasonLeaderboard from './components/PublicSeasonLeaderboard';
import PublicBracket from './components/PublicBracket';
import PublicTeams from './components/PublicTeams';
import About from './components/About';
import Wiki from './components/Wiki';
import VRSRankings from './components/VRSRankings';
import SteamStatus from './components/steamstatus/SteamStatus';
import Settings from './components/Settings';
import TechPauseToast from './components/TechPauseToast';
import SystemHealthToast from './components/SystemHealthToast';
import ConnectionToast from './components/ConnectionToast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import NotFound from './components/NotFound';
import ErrorBoundary from './components/ErrorBoundary';
import VersionManager from './components/VersionManager';
import ForceChangePassword from './components/ForceChangePassword';
import Veto from './components/Veto';
import CoachTimeout from './components/CoachTimeout';
import LiveLogViewer from './components/LiveLogViewer';
import ApiAccess from './components/ApiAccess';
import ExtTournaments from './components/ext/ExtTournaments';
import ExtTournamentDetail from './components/ext/ExtTournamentDetail';
import ExtMatchView from './components/ext/ExtMatchView';
import ExtTournamentStats from './components/ext/ExtTournamentStats';
import ExtVeto from './components/ext/ExtVeto';
import ExtTeamEdit from './components/ext/ExtTeamEdit';
import ExtTeamSeating from './components/ext/ExtTeamSeating';
import ExtMatchSeating from './components/ext/ExtMatchSeating';
import TeamSeatingPage from './components/seating/TeamSeatingPage';
import MatchSeatingPage from './components/seating/MatchSeatingPage';
import MatchLiveView from './components/match/MatchLiveView';
import LanEnvironments from './components/lan/LanEnvironments';
import LanEventDetail from './components/lan/LanEventDetail';
import FileManager from './components/files/FileManager';
import { pageWrapVariants } from './animations';
import './App.css';

// Prevent requests from hanging forever (e.g. server down or slow)
axios.defaults.timeout = 30000;

// Guards the public /about route. When a super admin has hidden the About page,
// direct visits to /about redirect to home. Fetches the public config flag on mount.
function AboutGuard({ children }) {
  const [state, setState] = React.useState('loading'); // 'loading' | 'show' | 'hide'
  React.useEffect(() => {
    let active = true;
    axios.get('/api/settings/public')
      .then(res => { if (active) setState(res.data?.hide_about_page === true ? 'hide' : 'show'); })
      .catch(() => { if (active) setState('show'); }); // default: show on error
    return () => { active = false; };
  }, []);
  if (state === 'loading') return null;
  if (state === 'hide') return <Navigate to="/" replace />;
  return children;
}

function PageWrap({ children }) {
  return (
    <motion.div
      variants={pageWrapVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {children}
    </motion.div>
  );
}

// `bare` skips the sidebar Layout — for full-page tools opened in their own tab.
function AdminRoute({ children, bare = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="App" style={{ minHeight: 'var(--app-vh, 100vh)', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'var(--bg-void)', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" />;
  }

  if (user.role !== 'admin' && user.role !== 'super_admin' && user.role !== 'panel_admin') {
    return <Navigate to="/" />;
  }

  return bare ? children : <Layout>{children}</Layout>;
}

function MatchRedirect() {
  const { id } = useParams();
  return <Navigate to={`/match/${id}`} replace />;
}

function AnimatedRoutes() {
  const location = useLocation();
  const { user, loading } = useAuth();
  // The season page's tabs are sub-paths (/admin/seasons/5/matches …): one key per season, so a tab
  // switch doesn't remount the page (no transition, no reload, unsaved Settings kept).
  // A bracket of the Brackets tab has its own address (/admin/seasons/5/brackets/12) - same page too.
  const routeKey = location.pathname
    .replace(/^(\/admin\/seasons\/\d+)(\/[^/]*)?(\/\d+)?\/?$/, '$1')
    .replace(/^\/admin\/settings(\/[^/]*)?\/?$/, '/admin/settings'); // settings tabs = one page

  return (
<AnimatePresence mode="wait">
      <Routes location={location} key={routeKey}>
        {/* Public Routes */}
        <Route path="/" element={<PageWrap><PublicMatches /></PageWrap>} />
        <Route path="/match/:id" element={<PageWrap><PublicMatches /></PageWrap>} />
        <Route path="/bracket/:id" element={<PageWrap><PublicBracket /></PageWrap>} />
        <Route path="/teams" element={<PageWrap><PublicTeams /></PageWrap>} />
        <Route path="/teams/:id" element={<PageWrap><PublicTeams /></PageWrap>} />
        <Route path="/about" element={<AboutGuard><PageWrap><About /></PageWrap></AboutGuard>} />
        <Route path="/veto/:roomId" element={<PageWrap><Veto /></PageWrap>} />
        <Route path="/season/:id" element={<PageWrap><PublicSeasonLeaderboard /></PageWrap>} />
        <Route path="/timeout/:token" element={<CoachTimeout />} />
        <Route path="/ext-veto/:key" element={<ExtVeto />} />

        {/* Admin Login */}
        <Route
          path="/admin"
          element={
            loading ? (
              <div className="App" style={{ minHeight: 'var(--app-vh, 100vh)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                Loading...
              </div>
            ) : user && (user.role === 'admin' || user.role === 'super_admin' || user.role === 'panel_admin') ? (
              <Navigate to="/admin/matches" replace />
            ) : (
              <PageWrap><AdminLogin /></PageWrap>
            )
          }
        />

        {/* Redirect old login routes to /admin */}
        <Route path="/admin/login" element={<Navigate to="/admin" replace />} />
        <Route path="/login" element={<Navigate to="/admin" replace />} />

        {/* Admin Routes */}
        <Route path="/admin/dashboard" element={<AdminRoute><PageWrap><Dashboard /></PageWrap></AdminRoute>} />
        <Route path="/admin/teams" element={<AdminRoute><PageWrap><Teams /></PageWrap></AdminRoute>} />
        <Route path="/admin/teams/:id/seating" element={<AdminRoute><PageWrap><TeamSeatingPage /></PageWrap></AdminRoute>} />
        <Route path="/admin/matches" element={<AdminRoute><PageWrap><Matches /></PageWrap></AdminRoute>} />
        <Route path="/admin/matches/new" element={<AdminRoute><PageWrap><CreateMatchPage /></PageWrap></AdminRoute>} />
        <Route path="/admin/matches/:id" element={<AdminRoute><PageWrap><MatchDetail /></PageWrap></AdminRoute>} />
        <Route path="/admin/matches/:id/live-logs" element={<AdminRoute bare><LiveLogViewer /></AdminRoute>} />
        <Route path="/admin/matches/:id/seating" element={<AdminRoute><PageWrap><MatchSeatingPage /></PageWrap></AdminRoute>} />
        <Route path="/admin/matches/:id/live" element={<AdminRoute><PageWrap><MatchLiveView /></PageWrap></AdminRoute>} />
        <Route path="/admin/servers" element={<AdminRoute><PageWrap><Servers /></PageWrap></AdminRoute>} />
        <Route path="/admin/servers/lan" element={<AdminRoute><PageWrap><LanEnvironments /></PageWrap></AdminRoute>} />
        <Route path="/admin/servers/lan/:id" element={<AdminRoute><PageWrap><LanEventDetail /></PageWrap></AdminRoute>} />
        <Route path="/admin/servers/:id/files" element={<AdminRoute><PageWrap><FileManager /></PageWrap></AdminRoute>} />
        <Route path="/admin/maps" element={<AdminRoute><PageWrap><Maps /></PageWrap></AdminRoute>} />
        <Route
          path="/admin/admins"
          element={
            <AdminRoute>
              {(() => {
                const userData = JSON.parse(localStorage.getItem('user') || '{}');
                // Sub-admins can't manage admins. Panel admins CAN — they manage
                // their own sub-admins (Admins.js renders a panel-admin-scoped view).
                if (userData.is_subadmin) return <Navigate to="/admin/dashboard" replace />;
                return <PageWrap><Admins /></PageWrap>;
              })()}
            </AdminRoute>
          }
        />
        <Route path="/admin/seasons" element={<AdminRoute><PageWrap><Seasons /></PageWrap></AdminRoute>} />
        {/* One page per season; tabs overview | matches | leaderboard | brackets | settings.
            The old /matches, /leaderboard and /brackets URLs open the matching tab. */}
        <Route path="/admin/seasons/:id/:tab?" element={<AdminRoute><PageWrap><SeasonPage /></PageWrap></AdminRoute>} />
        <Route path="/admin/seasons/:id/:tab/:sub" element={<AdminRoute><PageWrap><SeasonPage /></PageWrap></AdminRoute>} />
        <Route path="/admin/wiki" element={<AdminRoute><PageWrap><Wiki /></PageWrap></AdminRoute>} />
        <Route path="/admin/settings/:tab?" element={<AdminRoute><PageWrap><Settings /></PageWrap></AdminRoute>} />
        <Route path="/admin/vrs-rankings" element={<AdminRoute><PageWrap><VRSRankings /></PageWrap></AdminRoute>} />
        <Route path="/admin/steam-status" element={<AdminRoute><PageWrap><SteamStatus /></PageWrap></AdminRoute>} />
        <Route path="/admin/api" element={<AdminRoute><PageWrap><ApiAccess /></PageWrap></AdminRoute>} />
        <Route path="/admin/tournaments" element={<AdminRoute><PageWrap><ExtTournaments /></PageWrap></AdminRoute>} />
        <Route path="/admin/tournaments/:id" element={<AdminRoute><PageWrap><ExtTournamentDetail /></PageWrap></AdminRoute>} />
        <Route path="/admin/tournaments/:id/stats" element={<AdminRoute><PageWrap><ExtTournamentStats /></PageWrap></AdminRoute>} />
        <Route path="/admin/tournaments/:id/matches/:mid" element={<AdminRoute><PageWrap><ExtMatchView /></PageWrap></AdminRoute>} />
        <Route path="/admin/tournaments/:id/matches/:mid/seating" element={<AdminRoute><PageWrap><ExtMatchSeating /></PageWrap></AdminRoute>} />
        <Route path="/admin/tournaments/:id/teams/new" element={<AdminRoute><PageWrap><ExtTeamEdit /></PageWrap></AdminRoute>} />
        <Route path="/admin/tournaments/:id/teams/:teamId/edit" element={<AdminRoute><PageWrap><ExtTeamEdit /></PageWrap></AdminRoute>} />
        <Route path="/admin/tournaments/:id/teams/:teamId/seating" element={<AdminRoute><PageWrap><ExtTeamSeating /></PageWrap></AdminRoute>} />

        {/* Redirect old routes */}
        <Route path="/teams" element={<Navigate to="/admin/teams" />} />
        <Route path="/matches" element={<Navigate to="/admin/matches" />} />
        <Route path="/matches/:id" element={<MatchRedirect />} />
        <Route path="/servers" element={<Navigate to="/admin/servers" />} />
        <Route path="/maps" element={<Navigate to="/admin/maps" />} />

        {/* 404 Catch-all */}
        <Route path="*" element={<PageWrap><NotFound /></PageWrap>} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <VersionManager>
          <Router>
            <AnimatedRoutes />
            <TechPauseToast />
            <SystemHealthToast />
            <ConnectionToast />
          </Router>
        </VersionManager>
      </ErrorBoundary>
      <ForceChangePassword />
    </AuthProvider>
  );
}

export default App;
